import React, { useState, useEffect } from "react";
import {
  KeyIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
} from "@heroicons/react/24/outline";
import { fetchMyApiKeys, setUserApiKey } from "../../services/user-service";
import { toast } from "react-toastify";

interface Props {
  isOpen?: boolean;
  onClose?: () => void;
  defaultApp?: "CLEMENTINO" | "PASSENGER" | "YANGZI";
}

export default function GatewayApiKeysModal({
  isOpen: propsIsOpen,
  onClose: propsOnClose,
  defaultApp = "CLEMENTINO",
}: Props) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"CLEMENTINO" | "PASSENGER" | "YANGZI">(defaultApp);
  const [keyInput, setKeyInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<{ clementino: boolean; passenger: boolean; yangzi: boolean }>({
    clementino: false,
    passenger: false,
    yangzi: false,
  });

  const isOpen = propsIsOpen !== undefined ? propsIsOpen : internalOpen;

  const handleClose = () => {
    if (propsOnClose) {
      propsOnClose();
    } else {
      setInternalOpen(false);
    }
  };

  const loadStatus = async () => {
    try {
      const res = await fetchMyApiKeys();
      setStatus({
        clementino: res.clementino,
        passenger: res.passenger,
        yangzi: res.yangzi,
      });
    } catch {
      // Ignorer silencieusement le statut en cas de probleme de session
    }
  };

  useEffect(() => {
    const handleKeyMissing = (e: any) => {
      const app = e.detail?.app || "CLEMENTINO";
      if (app === "CLEMENTINO" || app === "PASSENGER" || app === "YANGZI") {
        setActiveTab(app);
      }
      setInternalOpen(true);
      toast.warn(e.detail?.message || `Cle d'API ${app} requise.`);
    };

    window.addEventListener("gateway_key_missing", handleKeyMissing);
    return () => window.removeEventListener("gateway_key_missing", handleKeyMissing);
  }, []);

  useEffect(() => {
    if (isOpen) {
      loadStatus();
      setKeyInput("");
    }
  }, [isOpen]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!keyInput.trim()) {
      toast.error("Veuillez saisir une cle d'API valide.");
      return;
    }

    setLoading(true);
    try {
      let user: any = null;
      try {
        const u = sessionStorage.getItem("user");
        if (u) user = JSON.parse(u);
      } catch {}

      const userId = user?.id || 1;
      await setUserApiKey(userId, activeTab, keyInput.trim());
      toast.success(`Cle d'API ${activeTab} configuree avec succes.`);
      setKeyInput("");
      await loadStatus();
      handleClose();
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de l'enregistrement de la cle d'API.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 p-4">
      <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 pb-4">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-purple-50 text-purple-600">
              <KeyIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-900">Cles d'API Passerelle</h3>
              <p className="text-xs text-gray-500">Connexion aux microservices Clementino, Passenger et Yangzi</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="mt-4 flex gap-2 border-b border-gray-100 pb-3">
          {(["CLEMENTINO", "PASSENGER", "YANGZI"] as const).map((tab) => {
            const isConfigured =
              tab === "CLEMENTINO" ? status.clementino : tab === "PASSENGER" ? status.passenger : status.yangzi;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  setActiveTab(tab);
                  setKeyInput("");
                }}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition-all ${
                  activeTab === tab
                    ? "bg-purple-600 text-white shadow-sm"
                    : "bg-gray-50 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {isConfigured ? (
                  <CheckCircleIcon className="h-4 w-4 text-emerald-400" />
                ) : (
                  <ExclamationCircleIcon className="h-4 w-4 text-amber-500" />
                )}
                <span>{tab}</span>
              </button>
            );
          })}
        </div>

        <form onSubmit={handleSave} className="mt-5 space-y-4">
          <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheckIcon className="mt-0.5 h-5 w-5 text-purple-600 shrink-0" />
              <div className="text-xs leading-5 text-gray-600">
                <span className="font-semibold text-gray-900">{activeTab}</span> requiert une cle d'API utilisateur
                delivree par l'application pour autoriser les requetes transitant par la passerelle Mopozzi.
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700">
              Cle d'API {activeTab}
            </label>
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Collez votre cle API ici"
              className="mt-1.5 block w-full rounded-xl border border-gray-300 px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 shadow-xs focus:border-purple-600 focus:outline-none focus:ring-1 focus:ring-purple-600"
              autoFocus
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-xl border border-gray-300 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-purple-600 px-5 py-2.5 text-xs font-semibold text-white shadow-xs hover:bg-purple-700 disabled:opacity-50"
            >
              {loading ? "Enregistrement..." : "Enregistrer la cle"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
